import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Star } from 'lucide-react-native';

import { DirectoryCardErrorBoundary } from './DirectoryCardErrorBoundary';
import { FeaturedExpertCard } from './featuredExpertCard';

import { TabRootScrollView } from '@/components/TabRootList';
import { Button, ButtonText } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { RefreshControl } from '@/components/ui/refresh-control';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import type { ActiveFeaturedExpertItem } from '@/services/getFeaturedExpertsV2';

const featuredExpertsSubtitle =
  'Experts in the spotlight for their recent work and achievements. Start an inquiry about a patient case or topic in their featured area.';

type FeaturedExpertsSceneProps = {
  featuredExperts: ActiveFeaturedExpertItem[];
  isLoading: boolean;
  isError: boolean;
  refreshing: boolean;
  onRefresh: () => Promise<void>;
  onBrowseAllExperts?: () => void;
};

/**
 * Loading skeleton for featured expert card.
 */
function FeaturedExpertCardSkeleton() {
  return (
    <Card variant="outline" className="p-4">
      <HStack className="items-center gap-3">
        <Skeleton className="h-12 w-12 rounded-full" />
        <VStack className="flex-1 gap-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-5 w-24 rounded-full" />
        </VStack>
      </HStack>
      <VStack className="mt-3 gap-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
      </VStack>
    </Card>
  );
}

/**
 * Featured experts scene component for the "Featured" tab.
 * Displays featured experts in full card format without filters.
 * Shows skeleton cards during loading and actionable empty state when no experts available.
 */
export function FeaturedExpertsScene({
  featuredExperts,
  isLoading,
  isError,
  refreshing,
  onRefresh,
  onBrowseAllExperts,
}: FeaturedExpertsSceneProps) {
  const showEmptyState = isError || featuredExperts.length === 0;

  const refreshControl = !isWeb ? (
    <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
  ) : undefined;

  const getSceneContent = () => {
    if (isLoading) {
      return (
        <VStack className="gap-4">
          <Text className="text-md leading-normal text-typography-700">
            {featuredExpertsSubtitle}
          </Text>
          <VStack className="gap-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <FeaturedExpertCardSkeleton key={`skeleton-${index}`} />
            ))}
          </VStack>
        </VStack>
      );
    }

    if (showEmptyState) {
      return (
        <VStack className="flex-1 items-center justify-center px-4">
          <VStack className="items-center gap-4">
            <Icon as={Star} size="xl" className="text-typography-400" />
            <Text className="text-center font-medium text-lg text-typography-900">
              Featured Experts Coming Soon
            </Text>
            <Text className="text-center text-sm text-typography-600">
              We&apos;re curating a selection of specialists with deep expertise in specific cancer
              types. Check back soon to discover highlighted experts.
            </Text>
            {onBrowseAllExperts ? (
              <Button size="sm" variant="outline" onPress={onBrowseAllExperts} className="mt-2">
                <ButtonText>Browse All Experts</ButtonText>
              </Button>
            ) : null}
          </VStack>
        </VStack>
      );
    }

    return (
      <VStack className="gap-4">
        <Text className="text-md leading-normal text-typography-700">
          {featuredExpertsSubtitle}
        </Text>
        <VStack className="gap-3">
          {featuredExperts.map((item) => (
            <DirectoryCardErrorBoundary
              key={`${item.expert.id}-${item.cancerType.id}`}
              expertId={item.expert.id}>
              <FeaturedExpertCard item={item} />
            </DirectoryCardErrorBoundary>
          ))}
        </VStack>
      </VStack>
    );
  };

  const content = getSceneContent();

  return (
    <TabRootScrollView
      className="flex-1"
      contentContainerStyle={{
        paddingBottom: 16,
        paddingHorizontal: 0,
        flexGrow: showEmptyState || isLoading ? 1 : undefined,
      }}
      showsVerticalScrollIndicator={false}
      refreshControl={refreshControl}>
      <VStack className="flex-1 px-5 pt-4">{content}</VStack>
    </TabRootScrollView>
  );
}
